import React, { useCallback, useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { FileRejection, useDropzone } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const dropzoneStyles = {
  alignItems: 'center',
  bgcolor: 'sunken',
  border: '2px dashed',
  borderColor: 'divider',
  borderRadius: 2,
  cursor: 'pointer',
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'center',
  minHeight: 280,
  p: 4,
  textAlign: 'center',
  transition: 'border-color 120ms ease',
  '&:hover': { borderColor: 'primary.main' },
};
const disabledDropzoneStyles = {
  ...dropzoneStyles,
  cursor: 'default',
  opacity: 0.65,
};
const uploadIconStyles = { color: 'text.secondary', fontSize: 56 };
const acceptedFiles = {
  'application/gpx+xml': ['.gpx'],
  'application/vnd.google-earth.kml+xml': ['.kml'],
  'application/xml': ['.gpx', '.kml'],
  'text/csv': ['.csv'],
  'text/tab-separated-values': ['.tsv'],
  'text/plain': ['.csv', '.tsv', '.gpx', '.kml'],
  'text/xml': ['.gpx', '.kml'],
  'application/vnd.ms-excel': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
};

interface ImportDropzoneProps {
  disabled?: boolean;
  onFile: (file: File) => void;
  onError: (message: string) => void;
}

export const ImportDropzone = React.memo(
  ({ disabled = false, onFile, onError }: ImportDropzoneProps) => {
    const intl = useIntl();
    const handleAccepted = useCallback(
      (files: File[]) => {
        if (files[0]) {
          onFile(files[0]);
        }
      },
      [onFile]
    );
    const handleRejected = useCallback(
      (rejections: FileRejection[]) => {
        const rejection = rejections[0];
        const message = rejection?.errors[0]?.message;

        onError(
          message ||
            intl.formatMessage({
              id: 'imports.dropzone.invalid',
              defaultMessage:
                'Select one GPX, KML, CSV, TSV, or XLSX file smaller than 15 MiB.',
            })
        );
      },
      [intl, onError]
    );
    const { getInputProps, getRootProps, isDragActive } = useDropzone({
      accept: acceptedFiles,
      disabled,
      maxFiles: 1,
      maxSize: MAX_FILE_SIZE,
      multiple: false,
      onDropAccepted: handleAccepted,
      onDropRejected: handleRejected,
    });
    const rootProps = useMemo(
      () =>
        getRootProps({
          'aria-label': intl.formatMessage({
            id: 'imports.dropzone.ariaLabel',
            defaultMessage: 'Upload find list',
          }),
        }),
      [getRootProps, intl]
    );
    const inputProps = useMemo(() => getInputProps(), [getInputProps]);

    return (
      <Box
        sx={disabled ? disabledDropzoneStyles : dropzoneStyles}
        {...rootProps}
      >
        <input {...inputProps} />
        <Stack alignItems="center" gap={1}>
          <UploadFileOutlinedIcon sx={uploadIconStyles} />
          <Typography fontWeight={600} variant="h6">
            {isDragActive ? (
              <FormattedMessage
                id="imports.dropzone.dropActive"
                defaultMessage="Drop the find list here"
              />
            ) : (
              <FormattedMessage
                id="imports.dropzone.drop"
                defaultMessage="Drop a find list here"
              />
            )}
          </Typography>
          <Typography color="text.secondary">
            <FormattedMessage
              id="imports.dropzone.help"
              defaultMessage="or click to select one GPX, KML, CSV, TSV, or XLSX file, up to 15 MiB"
            />
          </Typography>
        </Stack>
      </Box>
    );
  }
);
